import { useMemo, useState } from 'react';

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DynamicGridDrawerRow,
  Icons,
  IconButton,
  Input,
  MultiAutocompleteBadgeCount,
  Stack,
  Typography,
  useDynamicGridContext,
} from '@cast/design-system';
import type { DynamicGridActionBarDrawerProps } from '@cast/design-system';

import type {
  WorkloadCategory,
  WorkloadCategoryFilterOption,
} from '../../_constants';
import { filterCustomizeDrawerItems, groupItemsByCategory } from './_utils';
import type { ItemCategoryMap } from './_utils';

export type InventoryDashboardCustomizeDrawerProps =
  DynamicGridActionBarDrawerProps & {
    itemCategories: ItemCategoryMap;
    categoryOptions: WorkloadCategoryFilterOption[];
  };

export const InventoryDashboardCustomizeDrawer = ({
  title = 'Customize dashboard',
  itemCategories,
  categoryOptions,
  ...props
}: InventoryDashboardCustomizeDrawerProps) => {
  const { items, hiddenItems, toggleItemVisibility, resetLayout } =
    useDynamicGridContext();

  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<
    WorkloadCategory[]
  >([]);

  const filteredItems = useMemo(
    () =>
      filterCustomizeDrawerItems({
        items,
        search,
        selectedCategories,
        itemCategories,
      }),
    [items, search, selectedCategories, itemCategories]
  );

  const groups = useMemo(
    () => groupItemsByCategory(filteredItems, itemCategories, categoryOptions),
    [filteredItems, itemCategories, categoryOptions]
  );

  const hasActiveFilters =
    search.trim().length > 0 || selectedCategories.length > 0;

  const clearFilters = () => {
    setSearch('');
    setSelectedCategories([]);
  };

  return (
    <Drawer
      size="small"
      testId="inventory-dashboard-customize-drawer"
      {...props}
    >
      <DrawerHeader>
        <Button
          size="sm"
          variant="ghost"
          startIcon={<Icons.ArrowCounterClockwise />}
          onClick={resetLayout}
          testId="restore-defaults-button"
          sx={{ ml: 'auto' }}
        >
          Restore default
        </Button>
      </DrawerHeader>
      <DrawerBody>
        <DrawerContent>
          <Typography variant="h5" mb={8}>
            {title}
          </Typography>
          <Typography variant="P12R" color="textSecondary" mb={24}>
            Select the cards you want to display, then drag them on the
            dashboard to rearrange.
          </Typography>

          <Stack
            display="grid"
            gridTemplateColumns="1fr minmax(140px, 160px) auto"
            gap={8}
            mb={16}
            alignItems="center"
          >
            <Input
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search for a widget"
              startAdornment={<Icons.MagnifyingGlass />}
              endAdornment={
                search ? (
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                    testId="clear-search-button"
                  >
                    <Icons.X />
                  </IconButton>
                ) : undefined
              }
              testId="customize-drawer-search"
            />
            <MultiAutocompleteBadgeCount<WorkloadCategory>
              size="small"
              indicatorLabel="View"
              placeholder="View"
              items={categoryOptions.map(({ value }) => value)}
              value={selectedCategories}
              showItemCheckbox
              getLabel={(value: WorkloadCategory) =>
                categoryOptions.find((option) => option.value === value)
                  ?.label ?? value
              }
              onChange={({
                selectedItems,
              }: {
                selectedItems: WorkloadCategory[];
              }) => {
                setSelectedCategories(selectedItems);
              }}
              testId="customize-drawer-category-filter"
            />
            <IconButton
              size="sm"
              variant="secondary"
              aria-label="Clear filters"
              disabled={!hasActiveFilters}
              onClick={clearFilters}
              testId="clear-filters-button"
            >
              <Icons.X />
            </IconButton>
          </Stack>
        </DrawerContent>

        {filteredItems.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            py={48}
            gap={12}
            data-testid="customize-drawer-empty"
          >
            <Icons.MagnifyingGlass size={48} />
            <Typography variant="P14M" color="textSecondary">
              No results found
            </Typography>
          </Stack>
        ) : (
          <Stack>
            {groups.map((group) => (
              <Stack key={group.category}>
                <Typography
                  variant="L10B"
                  color="textSecondary"
                  px={24}
                  pt={16}
                  pb={8}
                >
                  {group.label}
                </Typography>
                {group.items.map((item) => (
                  <DynamicGridDrawerRow
                    key={item.id}
                    item={item}
                    visible={!hiddenItems.has(item.id)}
                    onToggle={() => toggleItemVisibility(item.id)}
                  />
                ))}
              </Stack>
            ))}
          </Stack>
        )}
      </DrawerBody>
    </Drawer>
  );
};

/** Stale-props-safe wrapper factory for DynamicGridActionBar.drawerComponent */
export const createInventoryCustomizeDrawer = (
  itemCategories: ItemCategoryMap,
  categoryOptions: WorkloadCategoryFilterOption[]
) => {
  const InventoryCustomizeDrawer = ({
    title,
    ...props
  }: DynamicGridActionBarDrawerProps) => (
    <InventoryDashboardCustomizeDrawer
      title={title}
      itemCategories={itemCategories}
      categoryOptions={categoryOptions}
      {...props}
    />
  );
  InventoryCustomizeDrawer.displayName = 'InventoryCustomizeDrawer';
  return InventoryCustomizeDrawer;
};
